import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Lock, CheckCircle2, AlertTriangle, ClipboardCheck, Building2,
  Calendar, TrendingUp, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import { getBackendUrl } from '../utils/config';

const API_URL = getBackendUrl();

const PublicSnelscanPage = () => {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [scanInfo, setScanInfo] = useState(null);
  const [scanData, setScanData] = useState(null);
  const [areas, setAreas] = useState([]);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [expandedAreas, setExpandedAreas] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchScanInfo();
    fetchAreas();
  }, [shareId]);

  const fetchScanInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/snelscan/${shareId}`);
      setScanInfo(res.data);
      setError(null);
    } catch (err) {
      setError('Snelscan niet gevonden of niet meer beschikbaar');
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/snelscan/areas`);
      setAreas(res.data);
    } catch (err) {
      console.error('Fout bij laden gebieden');
    }
  };

  const handleVerifyPassword = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/snelscan/${shareId}/verify`, {
        password
      });
      setScanData(res.data);
      setAuthenticated(true);
      toast.success('Toegang verleend');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Onjuist wachtwoord');
    } finally {
      setVerifying(false);
    }
  };

  const toggleArea = (areaId) => {
    setExpandedAreas(prev => ({ ...prev, [areaId]: !prev[areaId] }));
  };

  const getAreaData = (areaId) => {
    return scanData?.areas?.find(a => a.area_id === areaId) || {};
  };

  const statusConfig = {
    niet_beoordeeld: { label: 'Niet beoordeeld', bg: 'bg-stone-100', text: 'text-stone-600', icon: '⚪' },
    goed: { label: 'Goed', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: '🟢' },
    aandacht: { label: 'Aandacht', bg: 'bg-amber-100', text: 'text-amber-700', icon: '🟡' },
    kritiek: { label: 'Kritiek', bg: 'bg-rose-100', text: 'text-rose-700', icon: '🔴' }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-orange-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#C2410C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-semibold mb-2">Niet gevonden</h2>
            <p className="text-[#57534E]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Password entry screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-gradient-to-br from-[#C2410C] to-[#EA580C] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ClipboardCheck className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Snelscan Rapport</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {scanInfo && (
              <div className="text-center space-y-2 pb-4 border-b">
                <div className="flex items-center justify-center gap-2 text-[#57534E]">
                  <Building2 className="w-4 h-4" />
                  <span className="font-medium">{scanInfo.business_name || scanInfo.name}</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-[#57534E]">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(scanInfo.date)}</span>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <Lock className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                Dit rapport is beveiligd met een wachtwoord. Vraag de eigenaar om het wachtwoord.
              </p>
            </div>

            <form onSubmit={handleVerifyPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Wachtwoord</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Voer wachtwoord in"
                  required
                  autoFocus
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#C2410C] hover:bg-[#9A3412]"
                disabled={verifying || !password}
              >
                {verifying ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verifiëren...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Bekijk Rapport
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Full scan view after authentication
  const assessedAreas = scanData?.areas?.filter(a => a.status !== 'niet_beoordeeld') || [];
  const goodCount = scanData?.areas?.filter(a => a.status === 'goed').length || 0;
  const attentionCount = scanData?.areas?.filter(a => a.status === 'aandacht').length || 0;
  const criticalCount = scanData?.areas?.filter(a => a.status === 'kritiek').length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-orange-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-[#C2410C] to-[#EA580C] rounded-xl flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1C1917]">Snelscan Rapport</h1>
              <p className="text-[#57534E]">
                {scanData.business_name || scanData.name} • {formatDate(scanData.date)}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Score Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="col-span-1 md:col-span-2 bg-white shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[#1C1917]">Totaalscore</h3>
                <Badge className={scanData.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                  {scanData.completed ? 'Voltooid' : 'In uitvoering'}
                </Badge>
              </div>
              <div className="flex items-end gap-4">
                <span className={`text-5xl font-bold ${
                  scanData.overall_score >= 70 ? 'text-emerald-600' :
                  scanData.overall_score >= 40 ? 'text-amber-600' :
                  scanData.overall_score > 0 ? 'text-rose-600' : 'text-stone-400'
                }`}>
                  {scanData.overall_score != null ? `${Math.round(scanData.overall_score)}%` : '-'}
                </span>
                <span className="text-[#57534E] pb-2">gemiddelde score</span>
              </div>
              <Progress 
                value={assessedAreas.length / 10 * 100} 
                className="h-2 mt-4"
              />
              <p className="text-sm text-[#57534E] mt-2">
                {assessedAreas.length} van 10 gebieden beoordeeld
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-md">
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-[#57534E] mb-2">Status Verdeling</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">🟢 Goed</span>
                  <span className="font-semibold text-emerald-600">{goodCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">🟡 Aandacht</span>
                  <span className="font-semibold text-amber-600">{attentionCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">🔴 Kritiek</span>
                  <span className="font-semibold text-rose-600">{criticalCount}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-md">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-[#C2410C]" />
              <p className="text-sm text-[#57534E]">Aanbeveling</p>
              <p className="font-semibold text-[#1C1917] mt-1">
                {criticalCount > 0 ? 'Directe actie nodig' : 
                 attentionCount > 2 ? 'Verbeterpunten aanpakken' : 
                 goodCount >= 7 ? 'Uitstekend bezig!' : 'Blijven monitoren'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Areas Detail */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-[#1C1917]">Beoordeling per Gebied</h2>
          
          {areas.map((area, index) => {
            const areaData = getAreaData(area.id);
            const status = areaData.status || 'niet_beoordeeld';
            const config = statusConfig[status];
            const checkedItems = areaData.checked_items || [];
            const isExpanded = expandedAreas[area.id];

            return (
              <Card 
                key={area.id}
                className={`bg-white shadow-md border-l-4 ${
                  status === 'goed' ? 'border-l-emerald-500' :
                  status === 'aandacht' ? 'border-l-amber-500' :
                  status === 'kritiek' ? 'border-l-rose-500' :
                  'border-l-stone-300'
                }`}
              >
                <div 
                  className="p-4 cursor-pointer hover:bg-stone-50 transition-colors"
                  onClick={() => toggleArea(area.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{area.icon}</span>
                      <div>
                        <h3 className="font-semibold text-[#1C1917]">
                          {index + 1}. {area.title}
                        </h3>
                        <p className="text-sm text-[#57534E]">
                          {checkedItems.length} items gemarkeerd
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`${config.bg} ${config.text}`}>
                        {config.icon} {config.label}
                      </Badge>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-[#57534E]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#57534E]" />
                      )}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <CardContent className="pt-0 border-t">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                      {/* Checkpoints */}
                      <div>
                        <h4 className="font-medium text-emerald-700 mb-3 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Waar op letten
                        </h4>
                        <div className="space-y-2">
                          {area.waar_op_letten.map((item, i) => {
                            const isChecked = checkedItems.includes(`check_${i}`);
                            return (
                              <div key={i} className={`flex items-start gap-3 p-2 rounded ${isChecked ? 'bg-emerald-50' : 'bg-stone-50'}`}>
                                <span className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${isChecked ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-stone-300'}`}>
                                  {isChecked && <CheckCircle2 className="w-3 h-3" />}
                                </span>
                                <span className={`text-sm ${isChecked ? 'text-emerald-700' : 'text-[#57534E]'}`}>
                                  {item}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Red Flags */}
                      <div>
                        <h4 className="font-medium text-rose-700 mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Red Flags Geïdentificeerd
                        </h4>
                        <div className="space-y-2">
                          {area.red_flags.map((flag, i) => {
                            const isFlagged = checkedItems.includes(`flag_${i}`);
                            return (
                              <div key={i} className={`flex items-start gap-3 p-2 rounded ${isFlagged ? 'bg-rose-50 border border-rose-200' : 'bg-stone-50'}`}>
                                <span className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${isFlagged ? 'bg-rose-500 border-rose-500 text-white' : 'border-stone-300'}`}>
                                  {isFlagged && <AlertTriangle className="w-3 h-3" />}
                                </span>
                                <span className={`text-sm ${isFlagged ? 'text-rose-700 font-medium' : 'text-[#57534E]'}`}>
                                  {flag}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Area Notes */}
                    {areaData.notes && (
                      <div className="mt-4 p-4 bg-stone-50 rounded-lg">
                        <h4 className="font-medium text-[#1C1917] mb-2">Notities</h4>
                        <p className="text-sm text-[#57534E] whitespace-pre-wrap">{areaData.notes}</p>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Summary Notes */}
        {scanData.summary_notes && (
          <Card className="bg-white shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Samenvattende Conclusies</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#57534E] whitespace-pre-wrap">{scanData.summary_notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-8 text-sm text-[#57534E]">
          <p>Dit rapport is gegenereerd met Het Horeca Dashboard</p>
          <p className="mt-1">Laatst bijgewerkt: {formatDate(scanData.updated_at)}</p>
        </div>
      </main>
    </div>
  );
};

export default PublicSnelscanPage;
